
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useEffect } from 'react';

const Particulier = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <Link to="/">
              <Button variant="ghost" className="mb-4">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour à l'accueil
              </Button>
            </Link>
          </div>

          {/* Hero Section */}
          <div className="text-center mb-16 reveal-on-scroll">
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-8">🕊️ L'Espace Particulier – Revenir à soi</h1>
            
            <div className="max-w-4xl mx-auto space-y-6 text-lg leading-relaxed">
              <p>
                Tu avances, tu tiens, tu portes… mais dedans, ça fatigue.<br />
                Quelque chose bloque. Tu ne sais plus très bien où tu en es.<br />
                Tu veux juste pouvoir souffler, poser ce que tu traverses, te retrouver.
              </p>
              
              <p className="text-xl text-primary font-medium">
                🌿 Ici, tu n'as rien à prouver.<br />
                Tu peux être floue, fatiguée, débordée, vulnérable.<br />
                Je t'ouvre un espace doux, stable, vivant — pour revenir à toi, en profondeur.
              </p>
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            
            {/* Hypnose intuitive */}
            <Card className="card-soft h-full flex flex-col reveal-on-scroll">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">Hypnose intuitive – Revenir à soi</CardTitle>
                <CardDescription className="text-[#5F4B8B] text-base font-medium">
                  Quand tu te sens perdue, figée, ou coupée de ton élan…
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <img 
                  src="/lovable-uploads/53eda520-ad96-43f1-bc4a-3ce95a806dac.png" 
                  alt="Hypnose intuitive pour particuliers" 
                  className="w-full h-48 object-cover rounded-md mb-4"
                />
                <p className="text-foreground/80 mb-4">
                  Je t'accompagne vers les racines d'un blocage pour libérer, clarifier, raviver ta lumière intérieure.
                </p>
                <div className="text-sm text-foreground/70">
                  <p>⏳ 1h à 1h30 • 💸 80 € • En visio ou à Étampes</p>
                </div>
              </CardContent>
              <CardFooter>
                <a href="https://calendly.com/meneora44/hypnose-classique-adulte-en-presentiel-ou-en-visio" target="_blank" rel="noopener noreferrer" className="w-full">
                  <Button className="btn-primary w-full flex gap-2 items-center">
                    <Calendar className="h-4 w-4" /> Prendre rendez-vous
                  </Button>
                </a>
              </CardFooter>
            </Card>

            {/* Soin énergétique complet */}
            <Card className="card-soft h-full flex flex-col reveal-on-scroll">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">Soin énergétique – Libération, réalignement, régénération</CardTitle>
                <CardDescription className="text-[#5F4B8B] text-base font-medium">
                  Pour les moments où tu es dispersée, épuisée, ou "chargée" sans raison.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <img 
                  src="/lovable-uploads/5abe2d4f-9dd6-47ef-9c4f-ec00e0bc4a24.png" 
                  alt="Soin énergétique complet" 
                  className="w-full h-48 object-cover rounded-md mb-4"
                />
                <p className="text-foreground/80 mb-4">
                  Ce soin agit sur ton axe, ton système subtil, tes mémoires profondes (transgénérationnelles, karmiques, émotionnelles). Je travaille en canal, avec ce qui est juste dans l'instant. Tu reçois, tout simplement.
                </p>
                <div className="text-sm text-foreground/70">
                  <p>⏳ 1h à 1h15 • 💸 80 € • Présentiel ou à distance</p>
                </div>
              </CardContent>
              <CardFooter>
                <a href="https://calendly.com/meneora44" target="_blank" rel="noopener noreferrer" className="w-full">
                  <Button className="btn-primary w-full flex gap-2 items-center">
                    <Calendar className="h-4 w-4" /> Réserver ce soin
                  </Button>
                </a>
              </CardFooter>
            </Card>

            {/* Accompagnement vibratoire */}
            <Card className="card-soft h-full flex flex-col reveal-on-scroll">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">Accompagnement vibratoire – Renaissance (finançable CPF)</CardTitle>
                <CardDescription className="text-[#5F4B8B] text-base font-medium">
                  Un accompagnement profond sur 4 mois, en visio ou en présentiel.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <img 
                  src="/lovable-uploads/7a03015c-8263-4d46-94a2-1647d315ab39.png" 
                  alt="Accompagnement vibratoire Renaissance" 
                  className="w-full h-48 object-cover rounded-md mb-4"
                />
                <p className="text-foreground/80 mb-4">
                  Plusieurs soins vibratoires, transmissions, rituels, guidance personnalisée et soutien à ton rythme. Un espace de transformation, de reconnexion, et d'engagement envers toi-même.
                </p>
                <div className="text-sm text-foreground/70">
                  <p>💸 Tarif : 2 400 € • 📍 En visio ou en présentiel</p>
                </div>
              </CardContent>
              <CardFooter>
                <a href="https://calendly.com/meneora/accompagnement-sur-4-mois-financable-par-le-cpf" target="_blank" rel="noopener noreferrer" className="w-full">
                  <Button className="btn-primary w-full flex gap-2 items-center">
                    <Calendar className="h-4 w-4" /> Découvrir l'accompagnement
                  </Button>
                </a>
              </CardFooter>
            </Card>

            {/* Soin enfants / ados */}
            <Card className="card-soft h-full flex flex-col reveal-on-scroll">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">Soin enfants / ados – Apaiser, recentrer, rassurer</CardTitle>
                <CardDescription className="text-[#5F4B8B] text-base font-medium">
                  Pour les jeunes sensibles, agités ou angoissés.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <img 
                  src="/lovable-uploads/0c4d00e2-6a4a-45d2-8605-857e0b54e805.png" 
                  alt="Soins énergétiques pour enfants et adolescents" 
                  className="w-full h-48 object-cover rounded-md mb-4"
                />
                <p className="text-foreground/80 mb-4">
                  Un espace doux et respectueux, adapté à leur rythme.
                </p>
                <div className="text-sm text-foreground/70">
                  <p>⏳ 45 min à 1h • 💸 50 € • En visio ou à Étampes</p>
                </div>
              </CardContent>
              <CardFooter>
                <a href="https://calendly.com/meneora/hypnoseenfants" target="_blank" rel="noopener noreferrer" className="w-full">
                  <Button className="btn-primary w-full flex gap-2 items-center">
                    <Calendar className="h-4 w-4" /> Prendre rendez-vous
                  </Button>
                </a>
              </CardFooter>
            </Card>

            {/* Nettoyage énergétique de lieu */}
            <Card className="card-soft h-full flex flex-col reveal-on-scroll">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">Nettoyage énergétique de lieu – Retrouver la paix chez soi</CardTitle>
                <CardDescription className="text-[#5F4B8B] text-base font-medium">
                  Tu ressens une présence lourde, un sommeil perturbé, ou une tension dans ton espace ?
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <img 
                  src="/lovable-uploads/f7889efd-b399-4fc7-81c3-9e5b3865d760.png" 
                  alt="Nettoyage énergétique de lieu" 
                  className="w-full h-48 object-cover rounded-md mb-4"
                />
                <p className="text-foreground/80 mb-4">
                  Je nettoie vibratoirement le lieu, ses mémoires, et ses habitants (humains, animaux, plantes). Tu reçois aussi des outils simples pour entretenir ton lieu ensuite : rituels, prières, fréquences, cristaux…
                </p>
                <div className="text-sm text-foreground/70">
                  <p>⏳ 1h à 2h • 💸 à partir de 180 € • Visio ou à domicile</p>
                </div>
              </CardContent>
              <CardFooter>
                <Link to="/nettoyage-lieux">
                  <Button variant="outline" className="w-full">
                    Découvrir cette prestation
                  </Button>
                </Link>
              </CardFooter>
            </Card>

          </div>

          {/* Call to Action */}
          <div className="bg-accent/10 p-8 rounded-lg text-center reveal-on-scroll">
            <h2 className="text-2xl font-semibold text-primary mb-6">✨ Tu ne sais pas par quoi commencer ?</h2>
            <p className="text-lg mb-6">
              Écris-moi, on en parle simplement.
            </p>
            <a href="https://wa.me/33607507412" target="_blank" rel="noopener noreferrer">
              <Button size="lg">
                Me contacter sur WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Particulier;
